import React from 'react';
import { Link } from 'react-router-dom';

const Section = ({ number, title, children }) => (
  <section className="space-y-3">
    <h2 className="text-lg font-bold text-white tracking-tight">
      {number}. {title}
    </h2>
    <div className="space-y-3 text-[15px] text-white/50 leading-relaxed">{children}</div>
  </section>
);

const Privacy = () => {
  const effectiveDate = 'June 1, 2025';

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="border-b border-white/5">
        <div className="max-w-3xl mx-auto px-6 py-8 flex items-center justify-between">
          <Link to="/" className="text-white/30 hover:text-white transition-colors text-sm font-medium tracking-wide">
            ← Back
          </Link>
          <span className="text-xs text-white/20 tracking-widest uppercase font-medium">Wingcue</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-16 space-y-12">
        {/* Title block */}
        <div className="space-y-3">
          <h1 className="text-4xl font-black text-white tracking-tighter">Privacy Policy</h1>
          <p className="text-sm text-white/25 font-medium">Effective Date: {effectiveDate}</p>
        </div>

        <p className="text-[15px] text-white/50 leading-relaxed border-l-2 border-[#E94E34]/40 pl-5">
          Your privacy is important to us. This Privacy Policy explains how Wingcue collects, uses, shares, and protects your personal information when you use our App.
        </p>

        <div className="space-y-10">
          <Section number="1" title="Information We Collect">
            <p><span className="text-white/70 font-semibold">Information You Provide:</span> When you create an account or use the App, we may collect your email address, username, and any preferences you set within the App.</p>
            <p><span className="text-white/70 font-semibold">Screenshots & Uploaded Content:</span> When you upload screenshots for analysis, we temporarily process the image content to generate AI suggestions. This content is processed in real time and is not permanently stored on our servers beyond what is necessary to deliver the result.</p>
            <p><span className="text-white/70 font-semibold">Usage Data:</span> We automatically collect information about how you interact with the App, including features used, session duration, device type, operating system version, and crash reports.</p>
            <p><span className="text-white/70 font-semibold">Purchase Information:</span> In-app purchases are processed entirely by Apple. We do not collect or store your payment card information. We may receive confirmation of your purchase status from Apple.</p>
          </Section>

          <Section number="2" title="How We Use Your Information">
            <p>We use the information we collect to:</p>
            <ul className="list-disc list-inside space-y-1.5 pl-2">
              <li>Provide, operate, and improve the App and its AI features.</li>
              <li>Process uploaded screenshots to generate conversation suggestions.</li>
              <li>Manage your account and authenticate your identity.</li>
              <li>Send transactional communications such as receipts or security alerts.</li>
              <li>Analyze usage patterns to improve App performance and user experience.</li>
              <li>Comply with legal obligations and enforce our Terms of Service.</li>
            </ul>
            <p>We do <span className="text-white/70 font-semibold">not</span> use your uploaded screenshots or personal conversations to train AI models without your explicit, opt-in consent.</p>
          </Section>

          <Section number="3" title="Data Retention">
            <p>Uploaded screenshots are processed in memory and are automatically deleted from our servers within 24 hours of upload. Account information is retained for as long as your account is active. You may request deletion of your account and associated data at any time by contacting us at support@wingcue.com.</p>
          </Section>

          <Section number="4" title="Sharing of Information">
            <p>We do not sell, rent, or trade your personal information. We may share information only in the following limited circumstances:</p>
            <ul className="list-disc list-inside space-y-1.5 pl-2">
              <li><span className="text-white/70 font-semibold">Service Providers:</span> Trusted third-party vendors who assist us in operating the App (e.g., cloud hosting, analytics), bound by confidentiality agreements.</li>
              <li><span className="text-white/70 font-semibold">AI Processing:</span> Content may be sent to third-party AI API providers (such as OpenAI) solely to generate your requested suggestions. These providers are contractually prohibited from using your data for their own training purposes.</li>
              <li><span className="text-white/70 font-semibold">Legal Requirements:</span> If required by law, court order, or governmental authority.</li>
              <li><span className="text-white/70 font-semibold">Business Transfers:</span> In connection with a merger, acquisition, or sale of assets, with appropriate notice to you.</li>
            </ul>
          </Section>

          <Section number="5" title="Data Security">
            <p>We implement industry-standard technical and organizational security measures to protect your information, including encryption in transit (TLS) and at rest, access controls, and regular security reviews. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.</p>
          </Section>

          <Section number="6" title="Children's Privacy">
            <p>Wingcue is not directed to children under the age of 17. We do not knowingly collect personal information from children under 17. If we become aware that a child under 17 has provided us with personal information, we will delete it promptly. If you believe a child has provided us with their information, please contact us at support@wingcue.com.</p>
          </Section>

          <Section number="7" title="Your Rights & Choices">
            <p>Depending on your location, you may have the following rights regarding your personal data:</p>
            <ul className="list-disc list-inside space-y-1.5 pl-2">
              <li><span className="text-white/70 font-semibold">Access:</span> Request a copy of the personal information we hold about you.</li>
              <li><span className="text-white/70 font-semibold">Correction:</span> Request correction of inaccurate or incomplete data.</li>
              <li><span className="text-white/70 font-semibold">Deletion:</span> Request deletion of your personal data ("right to be forgotten").</li>
              <li><span className="text-white/70 font-semibold">Portability:</span> Request a machine-readable copy of your data.</li>
              <li><span className="text-white/70 font-semibold">Opt-out:</span> Opt out of any optional data processing, such as analytics.</li>
            </ul>
            <p>To exercise any of these rights, contact us at support@wingcue.com. We will respond within 30 days.</p>
          </Section>

          <Section number="8" title="Push Notifications">
            <p>We may send push notifications to your device if you grant permission. You can disable push notifications at any time through your device's system settings without affecting your ability to use the App.</p>
          </Section>

          <Section number="9" title="Analytics & Tracking">
            <p>We use anonymized, aggregated analytics to understand how users interact with our App. This data does not identify you personally. We do not use cross-app or cross-website tracking for advertising purposes.</p>
          </Section>

          <Section number="10" title="International Data Transfers">
            <p>Wingcue is operated in the United States. If you are located outside the U.S., please be aware that your information may be transferred to, stored, and processed in the U.S. By using the App, you consent to this transfer. We take steps to ensure your data is treated securely and in accordance with this Privacy Policy.</p>
          </Section>

          <Section number="11" title="California Privacy Rights (CCPA)">
            <p>If you are a California resident, you have the right to know what personal information we collect, the right to delete your personal information, and the right not to be discriminated against for exercising your rights. We do not sell personal information as defined under the CCPA. To submit a request, contact us at support@wingcue.com.</p>
          </Section>

          <Section number="12" title="Changes to This Policy">
            <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by updating the "Effective Date" above and, where appropriate, through an in-app notification. Your continued use of the App after changes are posted constitutes your acceptance of the updated policy.</p>
          </Section>

          <Section number="13" title="Contact Us">
            <p>If you have any questions, concerns, or requests regarding this Privacy Policy, please contact us at:</p>
            <p className="text-white/60 font-medium">support@wingcue.com</p>
          </Section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;